/**
 * Meta (Facebook) Pixel Tracker
 *
 * Provides Meta Pixel event tracking integration for WooAI Chatbot Pro.
 * Automatically detects existing Meta Pixel installation and tracks chatbot events.
 *
 * @package WooAIChatbot
 * @since 0.2.0
 */
/**
 * Internal event to Meta standard event mapping
 */
const STANDARD_EVENT_MAP = {
    // Direct mappings to standard events
    product_view: 'ViewContent',
    product_click: 'ViewContent',
    add_to_cart: 'AddToCart',
    remove_from_cart: null, // Custom event
    add_to_wishlist: 'AddToWishlist',
    checkout_start: 'InitiateCheckout',
    purchase: 'Purchase',
    search: 'Search',
    contact: 'Contact',
    lead: 'Lead',
    page_view: 'PageView',
    // Chat events - custom
    conversation_start: null,
    widget_open: null,
    widget_close: null,
    message_sent: null,
    message_received: null,
    session_start: null,
    session_end: null,
    topic_detected: null,
    playbook_started: null,
    playbook_completed: null,
};
/**
 * Meta Pixel Tracker Class
 *
 * Singleton class for tracking events to Meta (Facebook) Pixel.
 * Automatically detects if Meta Pixel is installed on the page.
 */
class MetaPixelTracker {
    constructor() {
        Object.defineProperty(this, "pixelId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isInitialized", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "debugMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
    }
    /**
     * Initialize the tracker with a Pixel ID
     *
     * @param pixelId - Meta Pixel ID (numeric string)
     * @param options - Optional configuration
     */
    init(pixelId, options) {
        if (!pixelId || typeof pixelId !== 'string') {
            console.warn('[MetaPixelTracker] Invalid Pixel ID provided');
            return;
        }
        // Validate Pixel ID format (numeric string)
        if (!/^\d+$/.test(pixelId)) {
            console.warn('[MetaPixelTracker] Pixel ID should be a numeric string');
        }
        this.pixelId = pixelId;
        this.debugMode = options?.debug ?? false;
        this.isInitialized = true;
    }
    /**
     * Check if Meta Pixel is available on the page
     *
     * @returns True if fbq function exists
     */
    isAvailable() {
        return typeof window.fbq === 'function';
    }
    /**
     * Check if tracker has been initialized
     *
     * @returns True if init() has been called with valid ID
     */
    isReady() {
        return this.isInitialized && this.pixelId !== null;
    }
    /**
     * Track a standard Meta Pixel event
     *
     * @param eventName - Standard event name
     * @param params - Optional event parameters
     */
    trackStandard(eventName, params) {
        if (!this.isAvailable()) {
            return;
        }
        try {
            if (params) {
                window.fbq?.('track', eventName, params);
            }
            else {
                window.fbq?.('track', eventName);
            }
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MetaPixelTracker] Error tracking standard event:', error);
            }
        }
    }
    /**
     * Track a custom event
     *
     * @param eventName - Custom event name
     * @param params - Optional event parameters
     */
    trackCustom(eventName, params) {
        if (!this.isAvailable()) {
            return;
        }
        if (!eventName || typeof eventName !== 'string') {
            console.warn('[MetaPixelTracker] Invalid event name');
            return;
        }
        // Format event name: PascalCase with WooAI prefix
        const formattedEventName = this.formatCustomEventName(eventName);
        try {
            if (params) {
                window.fbq?.('trackCustom', formattedEventName, params);
            }
            else {
                window.fbq?.('trackCustom', formattedEventName);
            }
        }
        catch (error) {
            if (this.debugMode) {
                console.error('[MetaPixelTracker] Error tracking custom event:', error);
            }
        }
    }
    /**
     * Track an event - automatically determines if standard or custom
     *
     * @param eventName - Internal event name
     * @param params - Optional event parameters
     */
    track(eventName, params) {
        const standardEvent = this.mapToStandardEvent(eventName);
        if (standardEvent) {
            this.trackStandard(standardEvent, params);
        }
        else {
            this.trackCustom(eventName, params);
        }
    }
    /**
     * Track ViewContent event for product views
     *
     * @param productId - Product ID
     * @param productName - Product name
     * @param value - Product value/price
     * @param currency - Currency code (default: USD)
     */
    trackViewContent(productId, productName, value, currency = 'USD') {
        const params = {
            content_ids: [String(productId)],
            content_name: productName,
            content_type: 'product',
        };
        if (typeof value === 'number') {
            params.value = value;
            params.currency = currency;
        }
        this.trackStandard('ViewContent', params);
    }
    /**
     * Track AddToCart event
     *
     * @param productId - Product ID
     * @param productName - Product name
     * @param value - Product value/price
     * @param currency - Currency code (default: USD)
     * @param quantity - Quantity added (default: 1)
     */
    trackAddToCart(productId, productName, value, currency = 'USD', quantity = 1) {
        const params = {
            content_ids: [String(productId)],
            content_name: productName,
            content_type: 'product',
            num_items: quantity,
            contents: [
                {
                    id: String(productId),
                    quantity,
                    item_price: value,
                },
            ],
        };
        if (typeof value === 'number') {
            params.value = value * quantity;
            params.currency = currency;
        }
        this.trackStandard('AddToCart', params);
    }
    /**
     * Track InitiateCheckout event
     *
     * @param contentIds - Array of product IDs in cart
     * @param value - Total cart value
     * @param currency - Currency code (default: USD)
     * @param numItems - Number of items
     */
    trackInitiateCheckout(contentIds, value, currency = 'USD', numItems) {
        const params = {
            content_ids: contentIds.map(String),
            content_type: 'product',
        };
        if (typeof value === 'number') {
            params.value = value;
            params.currency = currency;
        }
        if (typeof numItems === 'number') {
            params.num_items = numItems;
        }
        this.trackStandard('InitiateCheckout', params);
    }
    /**
     * Track Purchase event
     *
     * @param contentIds - Array of purchased product IDs
     * @param value - Order total value
     * @param currency - Currency code (default: USD)
     * @param contents - Detailed contents array
     */
    trackPurchase(contentIds, value, currency = 'USD', contents) {
        const params = {
            content_ids: contentIds.map(String),
            content_type: 'product',
            value,
            currency,
        };
        if (contents) {
            params.contents = contents;
            params.num_items = contents.reduce((sum, item) => sum + (item.quantity ?? 1), 0);
        }
        this.trackStandard('Purchase', params);
    }
    /**
     * Track Search event
     *
     * @param searchQuery - Search query string
     * @param contentIds - Optional matching product IDs
     */
    trackSearch(searchQuery, contentIds) {
        const params = {
            search_string: searchQuery,
            content_type: 'product',
        };
        if (contentIds && contentIds.length > 0) {
            params.content_ids = contentIds.map(String);
        }
        this.trackStandard('Search', params);
    }
    /**
     * Track Lead event (conversation that shows buying intent)
     *
     * @param params - Optional lead parameters
     */
    trackLead(params) {
        this.trackStandard('Lead', {
            content_name: 'WooAI Chatbot Lead',
            content_type: 'chat_lead',
            ...params,
        });
    }
    /**
     * Track Contact event (user reaching out)
     *
     * @param params - Optional contact parameters
     */
    trackContact(params) {
        this.trackStandard('Contact', {
            content_name: 'WooAI Chatbot Contact',
            content_type: 'chat_contact',
            ...params,
        });
    }
    /**
     * Map internal event name to Meta standard event
     *
     * @param event - Internal event name
     * @returns Meta standard event name or null for custom events
     */
    mapToStandardEvent(event) {
        return STANDARD_EVENT_MAP[event] ?? null;
    }
    /**
     * Format custom event name to PascalCase with WooAI prefix
     *
     * @param eventName - Raw event name
     * @returns Formatted event name
     */
    formatCustomEventName(eventName) {
        // Convert to PascalCase
        const pascalCase = eventName
            .split(/[_\s-]+/)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join('');
        // Add WooAI prefix if not already present
        if (!pascalCase.startsWith('WooAI')) {
            return `WooAI${pascalCase}`;
        }
        return pascalCase;
    }
    /**
     * Enable debug mode
     */
    enableDebug() {
        this.debugMode = true;
    }
    /**
     * Disable debug mode
     */
    disableDebug() {
        this.debugMode = false;
    }
    /**
     * Get current configuration
     */
    getConfig() {
        return {
            pixelId: this.pixelId,
            isInitialized: this.isInitialized,
            isAvailable: this.isAvailable(),
        };
    }
}
/**
 * Singleton instance export
 */
export const metaPixelTracker = new MetaPixelTracker();
/**
 * Default export for convenience
 */
export default metaPixelTracker;
